import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  useFrappeEventListener,
  useFrappeGetCall,
  useFrappePostCall,
} from "frappe-react-sdk";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

import { getLocale } from "../../lib/i18n";
import {
  ADMIN_NOTIFICATIONS_PATH,
  isAdminPath,
  isOwnerPath,
  MANAGER_NOTIFICATIONS_PATH,
  OWNER_NOTIFICATIONS_PATH,
} from "../../lib/portal";
import { showNotificationToast } from "./showNotificationToast";

export interface NotificationLogItem {
  name: string;
  subject?: string;
  read?: 0 | 1 | boolean;
  creation?: string;
  modified?: string;
  document_type?: string;
  document_name?: string;
  link?: string;
  from_user?: string;
  type?: string;
  of_event_code?: string;
  of_category?: string;
}

export interface NotificationViewItem extends NotificationLogItem {
  subject: string;
  relativeTime: string;
  path: string | null;
  isUnread: boolean;
  eventCode: string | null;
  category: string | null;
}

interface NotificationLogsResponse {
  notification_logs: NotificationLogItem[];
  user_info?: Record<string, { fullname?: string; image?: string }>;
  has_more?: boolean;
  offset?: number;
  limit?: number;
}

interface UnreadCountResponse {
  count: number;
}

interface RealtimeNotificationPayload {
  name?: string;
  subject?: string;
  link?: string;
  event_code?: string;
  category?: string;
  document_type?: string;
  document_name?: string;
}

function stripHtml(value?: string): string {
  if (!value) return "";
  return value.replace(/<[^>]*>/g, "").trim();
}

function formatRelativeTime(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = date.getTime() - Date.now();
  const diffMinutes = Math.round(diffMs / 60_000);
  const rtf = new Intl.RelativeTimeFormat(getLocale(), { numeric: "auto" });
  const absMinutes = Math.abs(diffMinutes);
  if (absMinutes < 60) return rtf.format(diffMinutes, "minute");
  const diffHours = Math.round(diffMinutes / 60);
  if (Math.abs(diffHours) < 24) return rtf.format(diffHours, "hour");
  const diffDays = Math.round(diffHours / 24);
  if (Math.abs(diffDays) < 7) return rtf.format(diffDays, "day");
  return new Intl.DateTimeFormat(getLocale(), { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function normalizeSpaLink(link: string): string | null {
  const raw = link.trim();
  if (!raw) return null;

  let path = raw;
  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    try {
      const url = new URL(raw);
      path = `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return null;
    }
  }

  if (path.startsWith("/ownerflow/")) path = path.slice("/ownerflow".length) || "/";
  else if (path === "/ownerflow") path = "/";
  if (!path.startsWith("/")) path = `/${path}`;
  return path;
}

export function resolveNotificationPath(
  link?: string,
  documentType?: string,
  documentName?: string,
  ownerPortal = false,
): string | null {
  const fromLink = normalizeSpaLink(link || "");
  if (fromLink) return fromLink;

  const managerRoutes: Record<string, string> = {
    "OF Booking": "/bookings",
    "OF Booking Request": "/network/requests",
    "OF Issue": "/issues",
    "OF Contact Request": "/admin/support/contact",
    "OF Founder Access Request": "/admin/support/founder",
    "OF Support Ticket": "/admin/support/tickets",
    "OF Quote Request": "/admin/support/quotes",
    "OF Payment": "/payments",
    "OF Rent Entry": "/rent-entries",
    "OF Owner Statement": "/owner-statements",
    "OF Property": "/properties",
    "OF Expense": "/expenses",
  };
  const ownerRoutes: Record<string, string> = {
    "OF Booking": "/owner/bookings",
    "OF Issue": "/owner/issues",
    "OF Expense": "/owner/expenses",
    "OF Owner Statement": "/owner/dashboard",
    "OF Property": "/owner/properties",
  };
  const routeByDoctype = ownerPortal ? ownerRoutes : managerRoutes;
  const base = documentType ? routeByDoctype[documentType] : undefined;
  if (!base) return null;
  if (base === "/owner/dashboard") return base;
  return documentName ? `${base}/${documentName}` : base;
}

function mapRow(row: NotificationLogItem, untitled: string, ownerPortal: boolean): NotificationViewItem {
  return {
    ...row,
    subject: stripHtml(row.subject) || untitled,
    relativeTime: formatRelativeTime(row.creation || row.modified),
    path: resolveNotificationPath(row.link, row.document_type, row.document_name, ownerPortal),
    isUnread: !row.read,
    eventCode: row.of_event_code || null,
    category: row.of_category || null,
  };
}

export function groupNotificationsByDay(
  items: NotificationViewItem[],
  labels: { today: string; yesterday: string; earlier: string },
): { label: string; items: NotificationViewItem[] }[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const buckets: Record<string, NotificationViewItem[]> = {
    today: [],
    yesterday: [],
    earlier: [],
  };

  for (const item of items) {
    const raw = item.creation || item.modified;
    const date = raw ? new Date(raw) : null;
    if (!date || Number.isNaN(date.getTime())) {
      buckets.earlier.push(item);
      continue;
    }
    const day = new Date(date);
    day.setHours(0, 0, 0, 0);
    if (day.getTime() === today.getTime()) buckets.today.push(item);
    else if (day.getTime() === yesterday.getTime()) buckets.yesterday.push(item);
    else buckets.earlier.push(item);
  }

  return (
    [
      { label: labels.today, items: buckets.today },
      { label: labels.yesterday, items: buckets.yesterday },
      { label: labels.earlier, items: buckets.earlier },
    ] as const
  ).filter((group) => group.items.length > 0);
}

export type NotificationCategoryFilter =
  | "all"
  | "bookings"
  | "issues"
  | "contacts"
  | "finance"
  | "network"
  | "other";

interface UseNotificationsOptions {
  limit?: number;
  unreadOnly?: boolean;
  category?: NotificationCategoryFilter | null;
  /** When true, load more pages into the same list (for /notifications page). */
  accumulate?: boolean;
  listPath?: string;
}

export function useNotifications(options: UseNotificationsOptions = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const ownerPortal = isOwnerPath(location.pathname);
  const adminPortal = isAdminPath(location.pathname);
  const listPath =
    options.listPath ??
    (adminPortal
      ? ADMIN_NOTIFICATIONS_PATH
      : ownerPortal
        ? OWNER_NOTIFICATIONS_PATH
        : MANAGER_NOTIFICATIONS_PATH);
  const limit = options.limit ?? 20;
  const unreadOnly = options.unreadOnly ?? false;
  const category = options.category && options.category !== "all" ? options.category : null;
  const accumulate = options.accumulate ?? false;

  const [offset, setOffset] = useState(0);
  const [accumulated, setAccumulated] = useState<NotificationViewItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const toastShownRef = useRef<string | null>(null);

  const listParams = useMemo(
    () => ({
      limit,
      offset,
      unread_only: unreadOnly ? 1 : 0,
      category: category || undefined,
    }),
    [limit, offset, unreadOnly, category],
  );

  const listKey = useMemo(
    () =>
      `notifications:list:${limit}:${unreadOnly ? 1 : 0}:${category || "all"}:${offset}`,
    [limit, unreadOnly, category, offset],
  );

  const logsCall = useFrappeGetCall<{ message: NotificationLogsResponse }>(
    "ownerflow.api.notifications.list_notifications",
    listParams,
    listKey,
  );

  const unreadCall = useFrappeGetCall<{ message: UnreadCountResponse }>(
    "ownerflow.api.notifications.get_unread_count",
    undefined,
    "notifications:unread-count",
  );

  const markAsReadCall = useFrappePostCall("ownerflow.api.notifications.mark_as_read");
  const markAsUnreadCall = useFrappePostCall("ownerflow.api.notifications.mark_as_unread");
  const markAllAsReadCall = useFrappePostCall("ownerflow.api.notifications.mark_all_as_read");

  const untitled = t("notifications.untitled");

  const pageItems = useMemo(() => {
    const rows = logsCall.data?.message?.notification_logs ?? [];
    return rows.map((row) => mapRow(row, untitled, ownerPortal));
  }, [logsCall.data, ownerPortal, untitled]);

  useEffect(() => {
    if (!accumulate) {
      setAccumulated([]);
      return;
    }
    if (logsCall.isLoading) return;
    const more = Boolean(logsCall.data?.message?.has_more);
    setHasMore(more);
    setAccumulated((prev) => {
      if (offset === 0) return pageItems;
      const seen = new Set(prev.map((item) => item.name));
      return [...prev, ...pageItems.filter((item) => !seen.has(item.name))];
    });
  }, [accumulate, logsCall.data, logsCall.isLoading, offset, pageItems]);

  // Reset pagination when filters change
  const filterKey = `${unreadOnly}:${category || "all"}`;
  const prevFilterRef = useRef(filterKey);
  useEffect(() => {
    if (prevFilterRef.current === filterKey) return;
    prevFilterRef.current = filterKey;
    setOffset(0);
    setAccumulated([]);
  }, [filterKey]);

  const notifications = accumulate ? accumulated : pageItems;

  const unreadCount = unreadCall.data?.message?.count ?? 0;

  const refresh = useCallback(() => {
    setOffset(0);
    void logsCall.mutate();
    void unreadCall.mutate();
  }, [logsCall, unreadCall]);

  const loadMore = useCallback(() => {
    if (!hasMore || logsCall.isLoading) return;
    setOffset((prev) => prev + limit);
  }, [hasMore, limit, logsCall.isLoading]);

  const showRichToast = useCallback(
    (payload: RealtimeNotificationPayload) => {
      const subject = stripHtml(payload.subject) || t("notifications.newReceived");
      const path = resolveNotificationPath(
        payload.link,
        payload.document_type,
        payload.document_name,
        ownerPortal,
      );
      const toastId = payload.name || "ownerflow-notification";
      if (toastShownRef.current === toastId) return;
      toastShownRef.current = toastId;

      showNotificationToast({
        id: toastId,
        title: t("notifications.title"),
        subject,
        viewLabel: t("notifications.view"),
        onView: () => {
          if (path) navigate(path);
          else navigate(listPath);
        },
      });
    },
    [listPath, navigate, ownerPortal, t],
  );

  const onRealtimeNotification = useCallback(
    (data?: RealtimeNotificationPayload) => {
      refresh();
      if (data && typeof data === "object" && (data.subject || data.name)) {
        showRichToast(data);
      } else {
        toast(t("notifications.newReceived"), { id: "ownerflow-notification" });
      }
    },
    [refresh, showRichToast, t],
  );

  useFrappeEventListener("ownerflow:notification", onRealtimeNotification);
  useFrappeEventListener("notification", () => {
    refresh();
  });

  const markAsRead = useCallback(
    async (docname: string) => {
      await markAsReadCall.call({ docname });
      refresh();
    },
    [markAsReadCall, refresh],
  );

  const markAsUnread = useCallback(
    async (docname: string) => {
      await markAsUnreadCall.call({ docname });
      refresh();
    },
    [markAsUnreadCall, refresh],
  );

  const markAllAsRead = useCallback(async () => {
    await markAllAsReadCall.call({});
    refresh();
  }, [markAllAsReadCall, refresh]);

  return {
    notifications,
    unreadCount,
    isLoading: logsCall.isLoading && offset === 0 && notifications.length === 0,
    isLoadingMore: logsCall.isLoading && offset > 0,
    hasMore: accumulate ? hasMore : Boolean(logsCall.data?.message?.has_more),
    markAsRead,
    markAsUnread,
    markAllAsRead,
    refresh,
    loadMore,
    userInfo: logsCall.data?.message?.user_info,
    listPath,
    ownerPortal,
    adminPortal,
  };
}
